import React, { useState, useRef, useEffect } from "react";
import "./DemoCall.css";
import { Phone, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import DemoCallService from "../../services/DemoCall.service";

const DemoCall = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    customerName: "",
    customerMobile: "",
    callType: "ulip",
  });

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);
  const [isCallInitialized, setIsCallInitialized] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [clientOptions, setClientOptions] = useState([]);
  const [productOptions, setProductOptions] = useState({});

  const [loadingOptions, setLoadingOptions] = useState(false);

  const formRef = useRef(null);



  const fetchClientOptions = async () => {
    setLoadingOptions(true);
    try {
      const res = await DemoCallService.getClientOptions();
      const options = res?.data.data || [];
      setClientOptions(options);
    } catch (err) {
      toast.error("Could not fetch company options. Please try again.");
      setClientOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  };
 const fetchProductOptions = async () => {
        try {
          const response = await DemoCallService.getProductOptions();
          
          setProductOptions(response?.data || []);
        } catch (err) {
          console.error("Error loading options:", err);
        }
      };

    useEffect(() => {
    fetchClientOptions();
    fetchProductOptions();
    fetchPolicyDetails("ulip");
  }, []);

  const policyDataMap = {
    ulip: {
      policy_number: "488182467",
      policy_type: "ULIP",
      policy_name: "ULIP Policy",
      status: "Discontinued",
      frequency: "Annual",
      annual_premium:"₹6,00,000",
      policy_term: "10 Years",
      premium_payment_term: "5 Years",
      start_date: "14 August, 2023",
      last_premium_due_date: "14 August, 2025",
      sum_assured: "₹60,00,000",
      paid_till_date: "₹12,00,000",
      pending_amount: "₹6,00,000",
      allocation: "Equity Fund 100%",
      effective_charges: "7.95%",
      fund_value: "₹15,90,000",
      maturity_effective_charges: "1.41% (@ 8% estimate)",
      maturity_fund_value: "₹51,50,000 (@ 8% estimate)",
       current_effective_returns:"11.47%",
      key_features: [
        "Policy Term: 10 Years",
        "Premium Payment Term: 5 Years",
        "Fund Allocation: Equity Fund 100%",
        "Current Fund Value: ₹15,90,000",
        "Effective Charges: 7.95%",
      ],
    },
    trad: {
      policy_number: "418254423",
      policy_type: "TRAD",
      policy_name: "TRAD Policy",
      status: "Lapsed",
      frequency: "Annual",
      annual_premium:"₹2,50,000",
      policy_term: "15 Years",
      premium_payment_term: "10 Years",
      start_date: "25 September, 2020",
      last_premium_due_date: "14 September, 2024",
      sum_assured: "₹8,00,000",
      paid_till_date: "₹10,00,000",
      pending_amount: "₹2,50,000",
      benefits_till_date: "₹17,00,000",
      benefits_pay_one: "₹22,50,000",
      benefits_pay_all: "₹44,87,000",
      key_features: [
        "Policy Term: 15 Years",
        "Premium Payment Term: 10 Years",
        "Benefits Till Date: ₹17,00,000",
        "Benefits Pay One: ₹22,50,000",
        "Benefits Pay All and Stay Till End: ₹44,87,000",
      ],
    },
  };

 

  const getProductName = (companyCode, callType) => {
  if (!callType) return "(Dynamic)";

  const code = companyCode?.toUpperCase?.() || "DEFAULT";

  const mapping =
    productOptions?.[code] ||
    productOptions?.DEFAULT ||
    {};

  return (
    mapping?.[callType] ||
    mapping?.ulip ||
    mapping?.trad ||
    "(Dynamic)"
  );
};


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear inline error for this field while user types
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCallTypeChange = (e) => {
    const callType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      callType,
    }));
    // Auto-update policy when call type changes (fetch from service)
    fetchPolicyDetails(callType);
    // Clear callType error
    setFormErrors((prev) => ({ ...prev, callType: "" }));
  };

   const fetchPolicyDetails = async (callType) => {
    setSelectedPolicy(null);
    setShowPolicyDetails(true);
    try {
      const policy = policyDataMap[callType];
      setSelectedPolicy(policy);
    } catch (err) {
      setSelectedPolicy(policyDataMap[callType]);
      toast.error("Could not load policy details.");
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.companyName.trim()) {
      errors.companyName = "Please enter company name";
    }
    if (!formData.customerName.trim()) {
      errors.customerName = "Please enter customer name";
    }
    if (!formData.customerMobile.trim()) {
      errors.customerMobile = "Please enter mobile number";
    } else if (!/^[0-9]{10}$/.test(formData.customerMobile.trim())) {
      errors.customerMobile = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.callType) {
      errors.callType = "Please select type of call (ULIP or TRAD)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInitiateCall = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsCallInitialized(true);

      // Call the backend API to initiate the demo call with snake_case payload
      const response = await DemoCallService.initiateCall({
        company_name: formData.companyName, 
        customer_name: formData.customerName,
        customer_number: formData.customerMobile,
        script_type: formData.callType,
      });

      // Prefer the server-provided message when available
      const successMsg = response?.data?.detail || "Demo call initiated! Bot will call shortly...";
      toast.success(successMsg);
      // Reset after 2 seconds
      setTimeout(() => {
        setIsCallInitialized(false);
      }, 2000);
    } catch (error) {
      const errMsg =
        error.response?.data?.detail || "Failed to initiate demo call. Please try again.";
      toast.error(errMsg);
      setIsCallInitialized(false);
    }
  };

  const scrollToForm = () => {
    if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="demo-call-container">
      {/* Header */}
      <div className="demo-call-header">
        <div className="header-content">
          <div className="demo-call-header-left">
            <img src="/assets/images/logo.png" alt="logo" className="demo-logo" />
            <div className="title-area">
              <h1>Insure Bot</h1>
              <p>Test our AI Powered bot with a demo call</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="demo-call-content">
        <div className="container-demo">
          <div className="demo-grid">
            {/* Form Section */}
            <div className="form-section">
              <div className="form-card">
                <h2 className="form-title">
                  <Phone size={24} /> Get Started with Demo Call
                </h2>

                <form className="demo-form" ref={formRef}>
                  {/* Company Name Dropdown */}
                  <div className="form-group">
                    <label htmlFor="companyName" className="form-label">
                      Company Name *
                    </label>
                    <select
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      disabled={loadingOptions}
                      className="form-input form-select"
                    >
                      <option value="">
                        {loadingOptions ? "Loading companies..." : "Select a company"}
                      </option>
                      {clientOptions.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.companyName && (
                      <div className="form-error">{formErrors.companyName}</div>
                    )}
                  </div>

                  {/* Customer Name */}
                  <div className="form-group">
                    <label htmlFor="customerName" className="form-label">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="form-input"
                    />
                    {formErrors.customerName && (
                      <div className="form-error">{formErrors.customerName}</div>
                    )}
                  </div>

                  {/* Mobile Number */}
                  <div className="form-group">
                    <label htmlFor="customerMobile" className="form-label">
                      Mobile Number (10 digits) *
                    </label>
                    <input
                      type="tel"
                      id="customerMobile"
                      name="customerMobile"
                      value={formData.customerMobile}
                      onChange={handleInputChange}
                      placeholder="Enter 10-digit mobile number"
                      className="form-input"
                      maxLength="10"
                    />
                    {formErrors.customerMobile && (
                      <div className="form-error">{formErrors.customerMobile}</div>
                    )}
                  </div>

                  {/* Call Type Radio Buttons */}
                  {/* <div className="form-group">
                    <label className="form-label">Product Type *</label>
                    <div className="radio-group">
                      <div className="radio-option">
                        <input
                          type="radio"
                          id="ulip"
                          name="callType"
                          value="ulip"
                          checked={formData.callType === "ulip"}
                          onChange={handleCallTypeChange}
                          className="radio-input"
                        />
                        <label htmlFor="ulip" className="radio-label">
                          <span className="radio-circle"></span>
                          ULIP
                        </label>
                      </div>

                      <div className="radio-option">
                        <input
                          type="radio"
                          id="trad"
                          name="callType"
                          value="trad"
                          checked={formData.callType === "trad"}
                          onChange={handleCallTypeChange}
                          className="radio-input"
                        />
                        <label htmlFor="trad" className="radio-label">
                          <span className="radio-circle"></span>
                          TRAD
                        </label>
                      </div>
                    </div>
                    {formErrors.callType && (
                      <div className="form-error">{formErrors.callType}</div>
                    )}
                  </div> */}
                  {/* Initiate Call Button */}
                  <button
                    type="button"
                    onClick={handleInitiateCall}
                    disabled={isCallInitialized}
                    className={`initiate-btn ${
                      isCallInitialized ? "calling" : ""
                    }`}
                  >
                    {isCallInitialized ? (
                      <>
                        <span className="pulse-dot"></span>
                        Calling...
                      </>
                    ) : (
                      <>
                        <Phone size={20} />
                        Initiate Demo Call
                      </>
                    )}
                  </button>

                  {/* Info Box */}
                  {/* <div className="info-box">
                    <AlertCircle size={18} />
                    <p>
                      The bot will call you on the provided number and explain
                      the policy details and highlight the benefits during the call. A maximum of two calls are allowed per number.
                        This number will only be used for calling you now and not for any other purpose.

                    </p>
                  </div> */}
                  {/* <div className="info-box">
                    <AlertCircle size={18} style={{color:"red"}} />
                    <p>
                    </p>
                  </div> */}

                  
                </form>
              </div>
            </div>

            {/* Policy Details Section */}
            <div className="policy-section">
                  {showPolicyDetails && selectedPolicy ? (
                <div className="policy-card">
                  <h2 className="policy-title">
                    {formData.callType === "ulip" ? "Sample Policy (ULIP) " : "TRAD"}
                  </h2>

                  <div className="policy-details">
                    {/* Policy Information */}
                    <div className="detail-group">
                      <h3>Policy Information</h3>
                      <div className="detail-row">
                          <span className="detail-label">Policy Number:</span>
                          <span className="detail-value">
                            {selectedPolicy.policy_number}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Product Name:</span>
                          <span className="detail-value">
                            {getProductName(formData.companyName, formData.callType)}
                          </span>
                        </div>
                      <div className="detail-row">
                        <span className="detail-label">Status:</span>
                        <span className="detail-value">
                          {selectedPolicy.status}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label"> Premium Frequency:</span>
                        <span className="detail-value">
                          {selectedPolicy.frequency}
                        </span>
                      </div>
                       <div className="detail-row">
                        <span className="detail-label">Annual Premium :</span>
                        <span className="detail-value">
                          {selectedPolicy.annual_premium}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Policy Term:</span>
                        <span className="detail-value">
                          {selectedPolicy.policy_term}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Premium Payment Term:</span>
                        <span className="detail-value">
                          {selectedPolicy.premium_payment_term}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Policy Start Date:</span>
                        <span className="detail-value">
                          {selectedPolicy.start_date}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Premium Due Date:</span>
                        <span className="detail-value">
                          {selectedPolicy.last_premium_due_date}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Sum Assured:</span>
                        <span className="detail-value">
                          {selectedPolicy.sum_assured}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Premium Paid Till Date:</span>
                        <span className="detail-value">
                          {selectedPolicy.paid_till_date}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Pending Premium Amount:</span>
                        <span className="detail-value">
                          {selectedPolicy.pending_amount}
                        </span>
                      </div>
                      {selectedPolicy.allocation && (
                        <div className="detail-row">
                          <span className="detail-label">Fund Allocation:</span>
                          <span className="detail-value">
                            {selectedPolicy.allocation}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* ULIP Specific - Content Engine Values */}
                    {/* {selectedPolicy.policy_type === "ULIP" && (
                      <>
                        <div className="detail-group">
                          <h3>Content Engine Values - Performance Till Date</h3>
                          <div className="detail-row">
                            <span className="detail-label">Effective Charges:</span>
                            <span className="detail-value">
                              {selectedPolicy.effective_charges}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Fund Value:</span>
                            <span className="detail-value">
                              {selectedPolicy.fund_value}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Currrent Effective Returns:</span>
                            <span className="detail-value">
                              {selectedPolicy.current_effective_returns}
                            </span>
                          </div>
                        </div>

                        <div className="detail-group">
                          <h3>Content Engine Values - Future Performance</h3>
                          <div className="detail-row">
                            <span className="detail-label">Effective Charges Till Maturity (@ 8% estimate):</span>
                            <span className="detail-value">
                              {selectedPolicy.maturity_effective_charges}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Fund Value At Maturity (@ 8% estimate):</span>
                            <span className="detail-value">
                              {selectedPolicy.maturity_fund_value}
                            </span>
                          </div>
                        </div>
                      </>
                    )} */}

                    {/* TRAD Specific - Content Engine Values */}
                    {selectedPolicy.policy_type === "TRAD" && (
                      <div className="detail-group">
                        <h3>Content Engine Values - Benefits Unlocked</h3>
                        <div className="detail-row">
                          <span className="detail-label">Till Date:</span>
                          <span className="detail-value">
                            {selectedPolicy.benefits_till_date}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Pay One More Premium and Stay Till Maturity:</span>
                          <span className="detail-value">
                            {selectedPolicy.benefits_pay_one}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Pay All Premiums and Stay Till Maturity:</span>
                          <span className="detail-value">
                            {selectedPolicy.benefits_pay_all}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* <div className="policy-note">
                    <AlertCircle size={16} />
                    <p>
                      This is sample policy data.
                    </p>
                  </div> */}
                </div>
              ) : (
                <div className="policy-placeholder">
                  <div className="placeholder-content">
                    <AlertCircle size={48} />
                    <p>Select a call type to view policy details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="demo-call-footer">
        <p>&copy; 2025 Insure Bot Demo. All rights reserved.</p>
      </div>
    </div>
  );
};

export default DemoCall;
